import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useAuth} from "../../contexts/AuthContext";
import {Dossier, Partenaire, Statut} from "../../../components/models";
import {getDossiersPrincipal, getDossiersSecondaire, getDossiersFilter} from "../../../components/requests";
import StatusBadge from "../../../components/common/StatusBadge";
import {formatCurrency, formatDate, getSourceReferencePrefix} from "../../../components/helpers";
import {IconFilter, IconReset, IconSpinner} from "../../../components/icons";
import Pagination from "../components/Pagination";
import {useFormik} from "formik";
import {useLocation, useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import PartenaireBadge from "../../../components/common/PartenaireBadge";
import {useHeader} from "../../contexts/HeaderContext";
import {SourceLogo} from "../../../components/common/SourceLogo";

const DossierPage = () => {
  const {isAdmin, isGestionnaire, currentUser, getPartenaire} = useAuth();
  const {setLastSearch} = useHeader();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isSecondaire,
    currentPage,
    dateAfter,
    dateBefore,
    source,
    reference,
    proprietaire,
    immatriculation,
    marque,
    modele,
    referencePartenaire,
    partenaire,
    statut,
  } = location.state || {};
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isListLoading, setIsListLoading] = useState<boolean>(false);
  const [dossiers, setDossiers] = useState<Dossier[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [partenaires, setPartenaires] = useState<Partenaire[]>([]);
  const [statuts, setStatuts] = useState<Statut[]>([]);
  const [page, setPage] = useState<number>((currentPage) ? currentPage : 1);
  const [secondaire,] = useState<boolean>(isSecondaire);
  const [dateAfterQuery,] = useState<string>((dateAfter) ? dateAfter : '');
  const [dateBeforeQuery,] = useState<string>((dateBefore) ? dateBefore : '');
  const [sourceQuery,] = useState<string>((source) ? source : '');
  const [referenceQuery,] = useState<string>((reference) ? reference : '');
  const [proprietaireQuery,] = useState<string>((proprietaire) ? proprietaire : '');
  const [immatriculationQuery,] = useState<string>((immatriculation) ? immatriculation : '');
  const [marqueQuery,] = useState<string>((marque) ? marque : '');
  const [modeleQuery,] = useState<string>((modele) ? modele : '');
  const [referencePartenaireQuery,] = useState<string>((referencePartenaire) ? referencePartenaire : '');
  const [partenaireQuery,] = useState<string>((partenaire) ? partenaire : '');
  const [statutQuery,] = useState<string>((statut) ? statut : '');

  // Données de recherche en state par navigation
  const searchFilter = {
    isSecondaire: isSecondaire,
    currentPage: page,
    dateAfter: dateAfter,
    dateBefore: dateBefore,
    source: source,
    reference: reference,
    proprietaire: proprietaire,
    immatriculation: immatriculation,
    marque: marque,
    modele: modele,
    referencePartenaire: referencePartenaire,
    partenaire: partenaire,
    statut: statut,
  };

  // Méthode centralisé de gestion de la page, en state et par pagination
  const setCurrentPage = (activePage: number) => {
    setPage(activePage);
    if (currentPage) location.state.currentPage = activePage;
  }

  // Méthode d'obtention de partenaire et status
  const getListDossiersFilter = useCallback(() => {
    getDossiersFilter()
      .then((response) => {
        if (response.status === 200 && response.data !== undefined) {
          let partenaireData: any;
          if (!isAdmin && isGestionnaire) {
            const nomPartenaire = getPartenaire()?.map(partenaire => partenaire.nom);
            partenaireData = response.data?.partenaires?.filter(partenaire => nomPartenaire?.includes(partenaire?.nom));
          } else partenaireData = response.data?.partenaires;
          setPartenaires(partenaireData);
          setStatuts(response.data?.statuts);
        }
      })
      .catch((error) => console.error(error));
  }, [getPartenaire, isGestionnaire, isAdmin]);

  // Méthode de recherche d'un dossier avec pagination
  // getDossiersPrincipal / getDossiersPrimaire recherche avec le filtre
  // getDossiersSecondaire recherche global
  const getListDossiers = useCallback(() => {
    setIsListLoading(true);

    // Synchronisation de la page avec les données en state
    if (currentPage && currentPage !== page) {
      setPage(currentPage);
      return;
    }

    const method = isSecondaire ? getDossiersSecondaire(
      (currentPage) ? currentPage : page,
      (!isAdmin && isGestionnaire) ? currentUser?.id?.toString() : '',
      reference?.trim(),
      source?.trim(),
      marque?.trim(),
      modele?.trim(),
      immatriculation?.trim(),
    ) : getDossiersPrincipal(
      (currentPage) ? currentPage : page,
      (!isAdmin && isGestionnaire) ? currentUser?.id?.toString() : '',
      /^[A-Za-z]{3}-\d{1,10}$/.test(reference) ? reference?.split('-')?.[1]?.trim() : reference?.trim(),
      statut?.trim(),
      partenaire?.trim(),
      marque?.trim(),
      modele?.trim(),
      immatriculation?.trim(),
      /^[A-Za-z]{3}-\d{1,10}$/.test(reference) ? reference?.split('-')?.[0]?.trim() : source?.trim(),
      dateAfter,
      dateBefore,
      referencePartenaire?.trim(),
      proprietaire?.trim(),
    );

    method
      .then((response) => {
        if (response.status === 200 && response.data !== undefined) {
          setTotalItems(response.data?.totalItems)
          setDossiers(response.data?.data)
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setIsLoading(false);
        setIsListLoading(false);
      });
  }, [
    isSecondaire,
    page,
    currentPage,
    dateAfter,
    dateBefore,
    source,
    reference,
    proprietaire,
    immatriculation,
    marque,
    modele,
    referencePartenaire,
    partenaire,
    statut,
    isGestionnaire,
    isAdmin,
    currentUser
  ]);

  // useEffect pour obtenir la liste des partenaires et status
  useEffect(() => {
    getListDossiersFilter();
  }, [getListDossiersFilter]);

  // useEffect pour afficher la liste et activer le filtre et la pagination
  useEffect(() => {
    getListDossiers();
  }, [getListDossiers]);

  // Méthode de soumission du filtre de recherche
  const formik = useFormik({
    initialValues: {
      dateAfter: (!secondaire && dateAfterQuery) ? dateAfterQuery : '',
      dateBefore: (!secondaire && dateBeforeQuery) ? dateBeforeQuery : '',
      dossier: (!secondaire && referenceQuery) ? (sourceQuery ? sourceQuery + '-' : '') + referenceQuery : '',
      proprietaire: (!secondaire && proprietaireQuery) ? proprietaireQuery : '',
      immatriculation: (!secondaire && immatriculationQuery) ? immatriculationQuery : '',
      marque: (!secondaire && marqueQuery) ? marqueQuery : '',
      modele: (!secondaire && modeleQuery) ? modeleQuery : '',
      referencePartenaire: (!secondaire && referencePartenaireQuery) ? referencePartenaireQuery : '',
      partenaire: (!secondaire && partenaireQuery) ? partenaireQuery : '',
      statut: (!secondaire && statutQuery) ? statutQuery : '',
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      // Suppression de la valeur du formulaire de recherche global
      setLastSearch('');

      // Si la date de début est supérieur à la date de fin
      if (values.dateAfter !== '' && values.dateBefore !== '') {
        if (new Date(values.dateAfter) > new Date(values.dateBefore)) {
          toast.error("La date de début est supérieur à la date de fin");
          return;
        }
      }

      // Navigation vers dossiers pour conserver dans l'historique de recherche dans navigate pour le bouton retour du navigateur
      navigate('/dossiers', {
        state: {
          isSecondaire: false,
          currentPage: 1,
          dateAfter: values?.dateAfter,
          dateBefore: values?.dateBefore,
          source: values?.dossier?.includes('-') ? values?.dossier?.split('-')?.[0] : '',
          reference: values?.dossier?.includes('-') ? values?.dossier?.split('-')?.[1] : values?.dossier,
          proprietaire: values?.proprietaire,
          immatriculation: values?.immatriculation,
          marque: values?.marque,
          modele: values?.modele,
          referencePartenaire: values?.referencePartenaire,
          partenaire: values?.partenaire,
          statut: values?.statut,
        },
        replace: true,
      });
    }
  });

  // Conservation de formik dans un useRef pour l'utiliser dans un useEffect
  const formikRef = useRef(formik);

  const resetAllForm = () => {
    formikRef.current.setFieldValue('dateAfter', '');
    formikRef.current.setFieldValue('dateBefore', '');
    formikRef.current.setFieldValue('dossier', '');
    formikRef.current.setFieldValue('proprietaire', '');
    formikRef.current.setFieldValue('immatriculation', '');
    formikRef.current.setFieldValue('marque', '');
    formikRef.current.setFieldValue('modele', '');
    formikRef.current.setFieldValue('referencePartenaire', '');
    formikRef.current.setFieldValue('partenaire', '');
    formikRef.current.setFieldValue('statut', '');
  }

  // Réinitialisation du formulaire si la recherche est globale
  useEffect(() => {
    // Si la méthode de recherche est primaire
    if (!isSecondaire) {
      setLastSearch('');
      return;
    }

    // Sinon nettoyer tout les formulaires du filtre
    resetAllForm();
  }, [isSecondaire, formikRef, setLastSearch]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen-fit">
        <IconSpinner className="animate-spin w-10 h-10"/>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen-fit animate-in fade-in duration-300">
      {/* Filtre début */}
      <div className="bg-gray-50 sticky top-0 py-3 px-4">
        <form onSubmit={formik.handleSubmit}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Tous les dossiers</h3>
              <p className="text-sm text-gray-500">{totalItems} dossiers au total</p>
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => resetAllForm()}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg text-sm font-medium"
              >
                <IconReset/>
              </button>

              <button
                type="submit"
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
              >
                <IconFilter/>
                Filtrer
              </button>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-4">
            <div>
              <label className="form-label">Début</label>
              <input
                name="dateAfter"
                type="date"
                className="w-full form-input"
                onChange={formik.handleChange}
                onKeyDown={(e) => {
                  if (e.key === "Delete") {
                    e.currentTarget.value = "";
                    formik.setFieldValue("dateAfter", '');
                  }
                }}
                value={formik.values.dateAfter}
              />
            </div>

            <div>
              <label className="form-label">Fin</label>
              <input
                name="dateBefore"
                type="date"
                className="w-full form-input"
                onChange={formik.handleChange}
                onKeyDown={(e) => {
                  if (e.key === "Delete") {
                    e.currentTarget.value = "";
                    formik.setFieldValue("dateBefore", '');
                  }
                }}
                value={formik.values.dateBefore}
              />
            </div>

            <div>
              <label className="form-label">Dossier</label>
              <input
                name="dossier"
                type="text"
                placeholder="XXX-0000"
                className="w-full form-input"
                onChange={formik.handleChange}
                value={formik.values.dossier}
              />
            </div>

            <div>
              <label className="form-label">Propriétaire</label>
              <input
                name="proprietaire"
                type="text"
                className="w-full form-input"
                onChange={formik.handleChange}
                value={formik.values.proprietaire}
              />
            </div>

            <div>
              <label className="form-label">Immatriculation</label>
              <input
                name="immatriculation"
                type="text"
                className="w-full form-input"
                onChange={formik.handleChange}
                value={formik.values.immatriculation}
              />
            </div>

            <div>
              <label className="form-label">Marque</label>
              <input
                name="marque"
                type="text"
                className="w-full form-input"
                onChange={formik.handleChange}
                value={formik.values.marque}
              />
            </div>

            <div>
              <label className="form-label">Modèle</label>
              <input
                name="modele"
                type="text"
                className="w-full form-input"
                onChange={formik.handleChange}
                value={formik.values.modele}
              />
            </div>

            <div>
              <label className="form-label">Référence Partenaire</label>
              <input
                name="referencePartenaire"
                type="text"
                className="w-full form-input"
                onChange={formik.handleChange}
                value={formik.values.referencePartenaire}
              />
            </div>

            <div>
              <label className="form-label">Partenaire</label>
              <select
                name="partenaire"
                className="w-full form-input form-select"
                onChange={formik.handleChange}
                value={formik.values.partenaire}
              >
                <option value="">Partenaire</option>
                {(partenaires.length > 0) && partenaires.map((partenaire, key) =>
                  <option key={key} value={partenaire.nom}>{partenaire.nom}</option>
                )}
              </select>
            </div>

            <div>
              <label className="form-label">Statut</label>
              <select
                name="statut"
                className="w-full form-input form-select"
                onChange={formik.handleChange}
                value={formik.values.statut}
              >
                <option value="">Statut</option>
                {(statuts.length > 0) && statuts.map((statut, key) =>
                  <option key={key} value={statut.libelle}>{statut.libelle}</option>
                )}
              </select>
            </div>
          </div>
        </form>
      </div>
      {/* Filtre fin */}

      <div className="px-4 space-y-4">
        {/* Dossiers début */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Début entête */}
          <div className="bg-gray-50 text-xs text-gray-500 uppercase border-b border-gray-200">
            <div className="table w-full table-fixed">
              <div className="table-row">
                <div className="px-5 py-3 text-left font-semibold table-cell w-[150px]">Dossier</div>
                <div className="px-5 py-3 text-left font-semibold table-cell">Véhicule</div>
                <div className="px-5 py-3 text-left font-semibold table-cell w-[100px]">Source</div>
                <div className="px-5 py-3 text-left font-semibold table-cell w-[200px]">Partenaire</div>
                <div className="px-5 py-3 text-left font-semibold table-cell w-[200px]">Statut</div>
                <div className="px-5 py-3 text-left font-semibold table-cell w-[150px]">Montant</div>
                <div className="px-5 py-3 text-left font-semibold table-cell w-[120px]">Action</div>
              </div>
            </div>
          </div>
          {/* Fin entête */}
          {/* Contenu du tableau scrollable seul */}
          <div className="max-h-[calc(100vh-27rem)] overflow-y-auto">
            <table className="w-full table-fixed min-w-full">
              <tbody className="divide-y divide-gray-100">
              {isListLoading ? (
                <tr>
                  <td colSpan={12}>
                    <div className="flex items-center justify-center min-h-[300px]">
                      <IconSpinner className="animate-spin w-10 h-10"/>
                    </div>
                  </td>
                </tr>
              ) : (dossiers.length === 0) ? (
                <tr>
                  <td colSpan={12}>
                    <div className="flex items-center justify-center min-h-[300px]">
                      <p className="text-gray-500">Pas de résultat</p>
                    </div>
                  </td>
                </tr>
              ) : (
                dossiers.length !== 0 && dossiers.map((dossier) => {
                  return (
                    <tr
                      key={dossier?.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/dossiers/gerer/${dossier?.id}`, {
                        state: {
                          ...searchFilter,
                          dossierData: dossier
                        }
                      })}
                    >
                      <td className="px-5 py-4 w-[150px]">
                        <p className="font-medium text-gray-900">
                          {getSourceReferencePrefix(dossier?.source?.libelle)}
                          {dossier?.reference}
                        </p>
                        <p className="text-xs text-gray-500">
                          {/* .toLocaleString("fr-FR") peux donner une imprécision à cause du fuseau horaire */}
                          {formatDate(dossier?.enlevements[0]?.dateOuvertureDossier)}
                        </p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="font-medium text-gray-900">
                          {dossier?.vehicule?.marque} / {dossier?.vehicule?.modele}
                        </p>
                        <p className="text-xs text-gray-500 font-mono">{dossier?.vehicule?.immatriculation}</p>
                      </td>
                      <td className="px-5 w-[100px]">
                        <p className="text-gray-500 font-mono">
                          <SourceLogo source={dossier?.source?.libelle} classExtra='w-10 h-10'/>
                        </p>
                      </td>
                      <td className="px-5 py-4 w-[200px]">
                        <PartenaireBadge nom={dossier?.partenaire?.nom}/>
                      </td>
                      {/*<td className="px-5 py-4">*/}
                      {/*  <p className="text-sm text-gray-900">{dossier.gestionnaire?.nom || '-'}</p>*/}
                      {/*</td>*/}
                      <td className="px-5 py-4 w-[200px]">
                        <StatusBadge statut={dossier?.statut?.libelle || ''}/>
                      </td>
                      <td className="px-5 py-4 w-[150px]">
                        <p
                          className="font-medium text-gray-900">{formatCurrency(dossier?.frais?.[0]?.fraisTotauxTTC || 0)}</p>
                      </td>
                      <td className="px-5 py-4 w-[120px]">
                        <button
                          className="px-3 py-1 bg-green-50 text-green-600 rounded text-xs font-medium hover:bg-green-100">
                          Afficher
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Dossiers fin */}

        {/* pagination début */}
        {!isListLoading && (
          <div className="bg-gray-50 sticky bottom-0 p-4">
            <Pagination totalItems={totalItems} step={25} page={currentPage ?? page} setPage={setCurrentPage}/>
          </div>
        )}
        {/* pagination fin */}
      </div>
    </div>
  );
}

export default DossierPage;